import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, AlertOctagon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { safeReplace } from "@/lib/safeNavigate";

// Iter332 · Bug 11a — Per-device UUID stored in localStorage so a
// helper pass's concurrent-cap check sees each phone as its own
// device even when they're on the same venue Wi-Fi with the same
// browser build (UA+IP hash used to collide → cap silently allowed
// unlimited devices).
export function getDeviceId() {
  try {
    let id = localStorage.getItem("rl_device_id");
    if (!id) {
      id = (crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem("rl_device_id", id);
    }
    return id;
  } catch { return `dev-fallback-${Date.now()}`; }
}

export const HELPER_ROLE_LABELS = {
  check_in_helper: "Front-Desk Check-In",
  court_ops_helper: "Court Operations",
  deputy_helper: "Deputy",
  festival_only_helper: "Deputy",
  // Iter431/437 · OPS Assist helper (internal name kept as
  // `roster_ops_helper` to avoid churn on stored passes and URL
  // paths). UI label is "OPS Assist".
  roster_ops_helper: "OPS Assist",
};

const ROLE_STORAGE_KEY = "rl_festival_helper_token";

export const helperLandingPath = (fid, role) => (
  role === "check_in_helper" ? `/festival/${fid}/players`
    : role === "roster_ops_helper" ? `/festival/${fid}/courts-ops`
      : `/festival/${fid}/live`
);

export function saveHelperSession({ festivalId, passId, token, role, label, expiresAt, playerReturn = null }) {
  // Iter445 — Persist `pass_id` so the api.js single-device kickout
  // interceptor can rebuild the `/festival/{fid}/helper/{pass_id}`
  // redeem URL and hard-redirect Phone A after wiping the stale
  // token. Previously only `festival_id` was stored, which left the
  // interceptor's `redirect` as null → toast fired but Phone A
  // stayed on OPS Assist.
  const doc = { festival_id: festivalId, pass_id: passId, token, role, label, expires_at: expiresAt, player_return: playerReturn };
  try { window.localStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(doc)); } catch {}
}

export function loadHelperSession() {
  try {
    const raw = window.localStorage.getItem(ROLE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expires_at && new Date(parsed.expires_at) < new Date()) return null;
    return parsed;
  } catch { return null; }
}

export function clearHelperSession() {
  try { window.localStorage.removeItem(ROLE_STORAGE_KEY); } catch {}
}

export default function FestivalHelperRedeem() {
  const { fid, passId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [code, setCode] = useState(params.get("code") || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Auto-redeem if the QR URL carried the code.
  useEffect(() => {
    const auto = params.get("code");
    if (auto && auto.length >= 4) submit(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (overrideCode) => {
    const c = (overrideCode ?? code).trim();
    if (c.length < 4) { setErr("Enter the access code from your organiser"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/festival/${fid}/helpers/redeem`, {
        pass_id: passId, code: c,
        // Iter332 · Bug 11a — Send the browser's stable device_id so
        // the backend concurrent-cap check can distinguish devices
        // on the same venue Wi-Fi + same UA.
        device_id: getDeviceId(),
      });
      saveHelperSession({
        festivalId: fid,
        passId: passId,
        token: r.data.helper_token,
        role: r.data.role,
        label: r.data.label,
        expiresAt: r.data.expires_at,
      });
      toast.success(`Signed in as ${HELPER_ROLE_LABELS[r.data.role] || r.data.role}`);
      // Iter331 · Bug 18 — Use safeReplace (hard nav) so the redemption
      // URL is popped off browser history entirely. Pressing back
      // from Live Ops / Player Manager now returns to whatever the
      // helper's device was on BEFORE the QR scan (usually a fresh
      // tab or their kiosk home), not `/login` — which was the
      // regression when React Router's soft `nav()` left the
      // `/helper/{passId}?code=...` entry in history and the login
      // wall bounced them off it.
      // Iter431 · Courts OPS helper lands on the panel-only roster
      // view — no court grid, no scheduler, no toolbar.
      safeReplace(helperLandingPath(fid, r.data.role));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Redeem failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-4" data-testid="helper-redeem-page">
      <div className="w-full max-w-md bg-[#141414] border border-[#262626] p-8">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="w-8 h-8 text-[#CCFF00]" />
          <div>
            <div className="label-mono text-[#CCFF00] mb-0.5">HELPER PASS</div>
            <h1 className="h-display text-2xl">Sign in for your shift</h1>
          </div>
        </div>
        <p className="text-sm text-[#A3A3A3] mb-4">
          Enter the 6-digit access code your organiser gave you. Your access is scoped to <span className="text-white">this festival only</span> and expires when the shift ends.
        </p>
        <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Access code</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={10}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          data-testid="helper-redeem-code"
          placeholder="••••••••"
          className="w-full text-2xl font-mono tracking-[0.35em] text-center bg-[#0A0A0A] border-2 border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-3 mb-3"
        />
        {err && (
          <div className="mb-3 text-xs text-[#FF4500] flex items-center gap-1" data-testid="helper-redeem-error">
            <AlertOctagon className="w-4 h-4" /> {err}
          </div>
        )}
        <button
          type="button"
          onClick={() => submit()}
          disabled={busy}
          data-testid="helper-redeem-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Verifying…" : (<>Unlock shift <ArrowRight className="w-4 h-4" /></>)}
        </button>
        <p className="text-[10px] uppercase tracking-widest text-[#525252] mt-4 text-center">
          Rally Lab · Festival Helper
        </p>
      </div>
    </div>
  );
}
